import { type ApiErrorResult } from '../types';
import { useLogStore } from '@scoring/state';
import { type InfoDetail } from '@scoring/types';

export const logApiError = (
    error: ApiErrorResult,
    operationType: 'mutation' | 'query',
    additionalContext?: Record<string, unknown>
): void => {
    const store = useLogStore.getState();

    const details: InfoDetail[] = [];

    details.push({ type: 'Operation Type', value: operationType });

    details.push({ type: 'HTTP Status', value: error.status.toString() });
    if (error.statusText) {
        details.push({ type: 'Status Text', value: error.statusText });
    }

    if (error.data) {
        details.push({ type: 'Error Message', value: error.data.message });

        // Quotable when reporting the failure - it is what ties this entry to the server log.
        if (error.data.correlationId) {
            details.push({ type: 'Correlation Id', value: error.data.correlationId });
        }
        if (error.data.requestId) {
            details.push({ type: 'Request Id', value: error.data.requestId });
        }
        if (error.data.detail && error.data.detail !== error.data.message) {
            details.push({ type: 'Detail', value: error.data.detail });
        }

        if (error.data.validationErrors && error.data.validationErrors.length > 0) {
            error.data.validationErrors.forEach((val, index) => {
                details.push({
                    type: `Validation Error ${index + 1}`,
                    value: `${val.field}: ${val.errors.join(', ')}`,
                });
            });
        }
    }

    try {
        const errorObj = error as unknown as Error;
        if (errorObj.stack) {
            details.push({ type: 'Error Stack Trace', value: errorObj.stack });
        }
    } catch {
        // No stack trace available
    }

    // Capture current call stack
    try {
        const stackTrace = new Error().stack;
        if (stackTrace) {
            // Remove the first few lines which are from this function and the error interceptor
            const lines = stackTrace.split('\n');
            const relevantLines = lines.slice(3).join('\n'); // Skip Error constructor, logApiError, and caller
            if (relevantLines.trim()) {
                details.push({ type: 'Call Stack', value: relevantLines });
            }
        }
    } catch {
        // Stack trace capture failed
    }

    if (additionalContext) {
        Object.entries(additionalContext).forEach(([key, value]) => {
            const stringValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
            details.push({ type: key, value: stringValue });
        });
    }

    details.push({ type: 'Timestamp', value: new Date().toISOString() });

    const sender = operationType;
    const message = `${error.status} - ${error.data?.message}`;

    const logEntry = {
        id: crypto.randomUUID(),
        sender,
        type: 'error' as const,
        message,
        details,
    };

    // Add to Zustand store
    store.addInfo(logEntry);
};
